import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import TransactionService from "@/services/TransactionService";
import {
  ChecklistService,
  type ChecklistItem as ServiceChecklistItem,
} from "@/services/checklistService";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Edit,
  FileText,
  Plus,
  Target,
  TrendingUp,
  X,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export interface ChecklistItem {
  id: string;
  label: string;
  category: string;
  required?: boolean;
  priority?: "high" | "medium" | "low";
  tip?: string;
  action?: {
    type:
      | "edit_asset"
      | "add_transaction"
      | "add_financial"
      | "view_transactions"
      | "view_documents";
    label: string;
    icon: React.ReactNode;
    categoryMetadata?: string; // Transaction/financial category to pre-select (e.g., 'RENT', 'MORTGAGE', 'UTILITIES')
  };
}

export interface ChecklistCategory {
  id: string;
  name: string;
  description: string;
  items: ChecklistItem[];
}

interface AssetChecklistProps {
  asset?: any;
  onItemToggle?: (itemId: string, completed: boolean) => void;
  onActionClick?: (
    action: string,
    itemId: string,
    categoryMetadata?: string
  ) => void;
  className?: string;
  variant?: "compact" | "full" | "floating";
}

const categories: ChecklistCategory[] = [
  {
    id: "purchase",
    name: "Purchase Information",
    description: "Essential purchase details and documentation",
    items: [
      {
        id: "purchase_price",
        label: "Purchase Price",
        category: "purchase",
        required: true,
        priority: "high",
        tip: "The total amount paid for the property",
        action: {
          type: "edit_asset",
          label: "Edit Asset",
          icon: <Edit className="h-4 w-4" />,
        },
      },
      {
        id: "purchase_date",
        label: "Purchase Date",
        category: "purchase",
        required: true,
        priority: "high",
        tip: "When you acquired the property",
        action: {
          type: "edit_asset",
          label: "Edit Asset",
          icon: <Edit className="h-4 w-4" />,
        },
      },
      {
        id: "closing_costs",
        label: "Closing Costs",
        category: "purchase",
        priority: "medium",
        tip: "Additional costs like fees, taxes, and insurance",
        action: {
          type: "edit_asset",
          label: "Edit Asset",
          icon: <Edit className="h-4 w-4" />,
        },
      },
    ],
  },
  {
    id: "financial",
    name: "Ongoing Financials",
    description: "Current financial performance and projections",
    items: [
      {
        id: "market_rent",
        label: "Market Rent",
        category: "financial",
        required: true,
        priority: "high",
        tip: "Current market rental rate for this property",
        action: {
          type: "edit_asset",
          label: "Edit Asset",
          icon: <Edit className="h-4 w-4" />,
        },
      },
      {
        id: "monthly_expenses",
        label: "Monthly Expenses",
        category: "financial",
        priority: "medium",
        tip: "Regular monthly costs like utilities, maintenance",
        action: {
          type: "add_financial",
          label: "Add Financial",
          icon: <Plus className="h-4 w-4" />,
          categoryMetadata: "UTILITIES", // Default to utilities, user can change
        },
      },
      {
        id: "cash_flow",
        label: "Cash Flow Analysis",
        category: "financial",
        priority: "high",
        tip: "Net income after all expenses",
        action: {
          type: "view_transactions",
          label: "View Transactions",
          icon: <TrendingUp className="h-4 w-4" />,
        },
      },
    ],
  },
  {
    id: "transactions",
    name: "Transaction History",
    description: "Track income, expenses, and property events",
    items: [
      {
        id: "rental_income",
        label: "Rental Income",
        category: "transactions",
        required: true,
        priority: "high",
        tip: "Record all rental payments received",
        action: {
          type: "add_transaction",
          label: "Add Transaction",
          icon: <Plus className="h-4 w-4" />,
          categoryMetadata: "RENT",
        },
      },
      {
        id: "expense_tracking",
        label: "Expense Tracking",
        category: "transactions",
        priority: "medium",
        tip: "Track all property-related expenses",
        action: {
          type: "add_transaction",
          label: "Add Transaction",
          icon: <Plus className="h-4 w-4" />,
        },
      },
      {
        id: "property_events",
        label: "Property Events",
        category: "transactions",
        priority: "low",
        tip: "Major events like renovations, sales",
        action: {
          type: "add_transaction",
          label: "Add Transaction",
          icon: <Plus className="h-4 w-4" />,
        },
      },
    ],
  },
];

const categoryConfig = {
  purchase: { color: "bg-blue-500", icon: <Target className="h-4 w-4" /> },
  financial: {
    color: "bg-green-500",
    icon: <TrendingUp className="h-4 w-4" />,
  },
  transactions: {
    color: "bg-purple-500",
    icon: <FileText className="h-4 w-4" />,
  },
};

export const AssetChecklist: React.FC<AssetChecklistProps> = ({
  asset,
  onItemToggle,
  onActionClick,
  className,
  variant = "full",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [userCompletedItems, setUserCompletedItems] = useState<Set<string>>(
    new Set()
  );
  const [transactions, setTransactions] = useState<any[]>([]);

  // Fetch transactions to check for transaction-related checklist items
  useEffect(() => {
    if (!asset?.id) {
      setTransactions([]);
      return;
    }

    const fetchTransactions = async () => {
      try {
        const data = await TransactionService.getAssetTransactions(asset.id);
        setTransactions(data);
      } catch (error) {
        console.error("Error fetching transactions for checklist:", error);
        setTransactions([]);
      }
    };

    fetchTransactions();
  }, [asset?.id]);

  // Check asset data to determine completed items
  const dataCompletedItems = new Set<string>();
  if (asset) {
    // Purchase Information
    if (asset.purchase_price) dataCompletedItems.add("purchase_price");
    if (asset.purchase_date) dataCompletedItems.add("purchase_date");
    if (asset.closing_costs) dataCompletedItems.add("closing_costs");

    // Financial Information
    if (asset.market_rent) dataCompletedItems.add("market_rent");
    if (asset.monthly_expenses) dataCompletedItems.add("monthly_expenses");
    if (asset.cash_flow !== undefined) dataCompletedItems.add("cash_flow");

    // Transaction History - check actual transactions
    const hasRentalIncome = transactions.some(
      (tx) => tx.transaction_type === "INCOME" && tx.category === "RENT"
    );
    if (hasRentalIncome) dataCompletedItems.add("rental_income");

    const hasExpenses = transactions.some(
      (tx) => tx.transaction_type === "EXPENSE"
    );
    if (hasExpenses) dataCompletedItems.add("expense_tracking");

    // Property events - check for major categories like CAPEX, renovations, etc.
    const hasPropertyEvents = transactions.some(
      (tx) =>
        tx.category === "CAPEX" ||
        tx.category === "MAINTENANCE" ||
        (tx.transaction_type === "EXPENSE" && tx.amount > 1000) // Major expenses
    );
    if (hasPropertyEvents) dataCompletedItems.add("property_events");
  }

  // Load user completed items from backend when asset changes
  useEffect(() => {
    if (!asset?.id) {
      setUserCompletedItems(new Set());
      return;
    }

    const loadUserChecklist = async () => {
      try {
        const response = await ChecklistService.getAssetChecklist(asset.id);
        // Convert the new items format to a Set of completed item IDs
        const completedItemIds = new Set(
          response.items
            .filter((item: ServiceChecklistItem) => item.completed)
            .map((item: ServiceChecklistItem) => item.item_id)
        );
        setUserCompletedItems(completedItemIds);
      } catch (error) {
        console.error("Error loading checklist:", error);
        // Fallback to empty set if API fails
        setUserCompletedItems(new Set());
      }
    };

    loadUserChecklist();
  }, [asset?.id]);

  // Combined completed items (data + user manual)
  const completedItems = new Set([
    ...dataCompletedItems,
    ...userCompletedItems,
  ]);

  const handleItemToggle = async (itemId: string) => {
    const newUserCompleted = new Set(userCompletedItems);
    const isCurrentlyCompleted = newUserCompleted.has(itemId);

    if (isCurrentlyCompleted) {
      newUserCompleted.delete(itemId);
    } else {
      newUserCompleted.add(itemId);
    }

    // Optimistically update UI
    setUserCompletedItems(newUserCompleted);
    onItemToggle?.(itemId, !isCurrentlyCompleted);

    // Save to backend
    if (asset?.id) {
      try {
        // Convert all items to the new format
        const allItems = categories.flatMap((cat) => cat.items);
        const checklistItems: ServiceChecklistItem[] = allItems.map((item) => ({
          item_id: item.id,
          completed:
            completedItems.has(item.id) || newUserCompleted.has(item.id),
          completed_at:
            completedItems.has(item.id) || newUserCompleted.has(item.id)
              ? new Date().toISOString()
              : undefined,
          notes: undefined,
        }));

        await ChecklistService.saveAssetChecklist({
          asset_id: asset.id,
          items: checklistItems,
        });
      } catch (error) {
        console.error("Error saving checklist:", error);
        toast.error("Failed to save checklist state");
        // Revert on error
        setUserCompletedItems(userCompletedItems);
      }
    }
  };

  const handleActionClick = (
    action: string,
    itemId: string,
    categoryMetadata?: string
  ) => {
    onActionClick?.(action, itemId, categoryMetadata);
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getCategoryProgress = (category: ChecklistCategory) => {
    const totalItems = category.items.length;
    const completedCount = category.items.filter((item) =>
      completedItems.has(item.id)
    ).length;
    return { completed: completedCount, total: totalItems };
  };

  const getOverallProgress = () => {
    const allItems = categories.flatMap((cat) => cat.items);
    const totalItems = allItems.length;
    const completedCount = allItems.filter((item) =>
      completedItems.has(item.id)
    ).length;
    return { completed: completedCount, total: totalItems };
  };

  const overallProgress = getOverallProgress();
  const progressPercentage =
    overallProgress.total > 0
      ? (overallProgress.completed / overallProgress.total) * 100
      : 0;

  // Floating button variant
  if (variant === "floating") {
    return (
      <>
        {/* Floating Button */}
        {!isOpen && (
          <Button
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "fixed right-0 top-1/2 -translate-y-1/2 z-50 w-10 h-50 rounded-l-lg shadow-lg border-r-0 flex flex-col items-center justify-center",
              progressPercentage === 100
                ? "bg-green-500 hover:bg-green-600"
                : progressPercentage >= 70
                  ? "bg-yellow-500 hover:bg-yellow-600"
                  : progressPercentage >= 40
                    ? "bg-orange-500 hover:bg-orange-600"
                    : "bg-red-500 hover:bg-red-600"
            )}
            size="sm"
          >
            <div className="flex flex-col items-center space-y-1">
              <ChevronLeft className="h-7 w-5" />
              <div className="text-xs font-medium text-center leading-tight text-white writing-mode-vertical">
                <div className="flex flex-col items-center space-y-0.5">
                  <span>C</span>
                  <span>H</span>
                  <span>E</span>
                  <span>C</span>
                  <span>K</span>
                  <span>L</span>
                  <span>I</span>
                  <span>S</span>
                  <span>T</span>
                </div>
              </div>
            </div>
          </Button>
        )}

        {/* Side Pane */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setIsOpen(false);
            }}
            // biome-ignore lint/a11y/useSemanticElements: Backdrop overlay cannot be a button as it contains interactive elements
            role="button"
            tabIndex={0}
            aria-label="Close checklist"
          >
            <div
              className="fixed right-0 top-0 h-full w-96 bg-background shadow-xl border-l"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold">Asset Checklist</h3>
                  <p className="text-sm text-muted-foreground">
                    {overallProgress.completed} of {overallProgress.total} items
                    completed
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="p-4 space-y-4 max-h-[calc(100vh-80px)] overflow-y-auto">
                {/* Overall Progress */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        Overall Progress
                      </span>
                      <Badge variant="secondary">
                        {overallProgress.completed}/{overallProgress.total}
                      </Badge>
                    </div>
                    <Progress value={progressPercentage} className="h-2" />
                  </CardContent>
                </Card>

                {/* Categories */}
                {categories.map((category) => {
                  const categoryProgress = getCategoryProgress(category);
                  const isExpanded = expandedCategories.has(category.id);
                  const config =
                    categoryConfig[category.id as keyof typeof categoryConfig];

                  if (!config) return null;

                  return (
                    <Card key={category.id} className="overflow-hidden">
                      <CardHeader
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleCategory(category.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleCategory(category.id);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={cn("p-1 rounded", config.color)}>
                              {config.icon}
                            </div>
                            <div>
                              <CardTitle className="text-base">
                                {category.name}
                              </CardTitle>
                              <p className="text-xs text-muted-foreground">
                                {category.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline">
                              {categoryProgress.completed}/
                              {categoryProgress.total}
                            </Badge>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </div>
                        </div>
                      </CardHeader>

                      {isExpanded && (
                        <CardContent className="pt-0">
                          <div className="space-y-2">
                            {category.items.map((item) => {
                              const isCompleted = completedItems.has(item.id);
                              const config =
                                categoryConfig[
                                  item.category as keyof typeof categoryConfig
                                ];

                              if (!config) return null;

                              return (
                                <button
                                  key={item.id}
                                  className="flex items-start space-x-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer w-full text-left"
                                  onClick={() => handleItemToggle(item.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleItemToggle(item.id);
                                    }
                                  }}
                                  type="button"
                                >
                                  <Checkbox
                                    checked={isCompleted}
                                    onChange={() => handleItemToggle(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <span
                                        className={cn(
                                          "text-sm font-medium",
                                          isCompleted &&
                                            "line-through text-muted-foreground"
                                        )}
                                      >
                                        {item.label}
                                      </span>
                                      {item.required && (
                                        <Badge
                                          variant="destructive"
                                          className="text-xs"
                                        >
                                          Required
                                        </Badge>
                                      )}
                                      {item.priority === "high" && (
                                        <Badge
                                          variant="secondary"
                                          className="text-xs"
                                        >
                                          High Priority
                                        </Badge>
                                      )}
                                    </div>
                                    {item.tip && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {item.tip}
                                      </p>
                                    )}
                                  </div>

                                  {!dataCompletedItems.has(item.id) &&
                                    item.action && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleActionClick(
                                            item.action?.type || "",
                                            item.id,
                                            item.action?.categoryMetadata
                                          );
                                        }}
                                        className="h-8 w-8 p-0"
                                      >
                                        {item.action.icon}
                                      </Button>
                                    )}
                                </button>
                              );
                            })}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Compact variant
  if (variant === "compact") {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Progress Checklist</span>
            <Badge variant="secondary">
              {overallProgress.completed}/{overallProgress.total}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {categories.map((category) => {
              const categoryProgress = getCategoryProgress(category);
              const isExpanded = expandedCategories.has(category.id);
              const config =
                categoryConfig[category.id as keyof typeof categoryConfig];

              if (!config) return null;

              return (
                <div key={category.id} className="space-y-2">
                  <button
                    className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors w-full text-left"
                    onClick={() => toggleCategory(category.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleCategory(category.id);
                      }
                    }}
                    type="button"
                  >
                    <div className="flex items-center space-x-2">
                      <div className={cn("p-1 rounded", config.color)}>
                        {config.icon}
                      </div>
                      <span className="text-sm font-medium">
                        {category.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="text-xs">
                        {categoryProgress.completed}/{categoryProgress.total}
                      </Badge>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="ml-6 space-y-2">
                      {category.items.map((item) => {
                        const isCompleted = completedItems.has(item.id);

                        return (
                          <button
                            key={item.id}
                            className="flex items-start space-x-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer w-full text-left"
                            onClick={() => handleItemToggle(item.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleItemToggle(item.id);
                              }
                            }}
                            type="button"
                          >
                            <Checkbox
                              checked={isCompleted}
                              onChange={() => handleItemToggle(item.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span
                                  className={cn(
                                    "text-sm",
                                    isCompleted &&
                                      "line-through text-muted-foreground"
                                  )}
                                >
                                  {item.label}
                                </span>
                                {item.required && (
                                  <Badge
                                    variant="destructive"
                                    className="text-xs"
                                  >
                                    Required
                                  </Badge>
                                )}
                                {item.priority === "high" && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    High Priority
                                  </Badge>
                                )}
                              </div>
                              {item.tip && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {item.tip}
                                </p>
                              )}
                            </div>

                            {!dataCompletedItems.has(item.id) &&
                              item.action && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleActionClick(
                                      item.action?.type || "",
                                      item.id,
                                      item.action?.categoryMetadata
                                    );
                                  }}
                                  className="h-8 w-8 p-0"
                                >
                                  {item.action.icon}
                                </Button>
                              )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full variant (default)
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Target className="h-5 w-5" />
          <span>Asset Checklist</span>
          <Badge variant="secondary">
            {overallProgress.completed}/{overallProgress.total}
          </Badge>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Track your progress on essential asset information and documentation
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Overall Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <Badge variant="secondary">
                {overallProgress.completed}/{overallProgress.total}
              </Badge>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>

          {/* Categories */}
          {categories.map((category) => {
            const categoryProgress = getCategoryProgress(category);
            const isExpanded = expandedCategories.has(category.id);
            const config =
              categoryConfig[category.id as keyof typeof categoryConfig];

            if (!config) return null;

            return (
              <Card key={category.id} className="overflow-hidden">
                <CardHeader
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={cn("p-1 rounded", config.color)}>
                        {config.icon}
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          {category.name}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {category.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">
                        {categoryProgress.completed}/{categoryProgress.total}
                      </Badge>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {category.items.map((item) => {
                        const isCompleted = completedItems.has(item.id);
                        const config =
                          categoryConfig[
                            item.category as keyof typeof categoryConfig
                          ];

                        if (!config) return null;

                        return (
                          <button
                            key={item.id}
                            className="flex items-start space-x-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer w-full text-left"
                            onClick={() => handleItemToggle(item.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleItemToggle(item.id);
                              }
                            }}
                            type="button"
                          >
                            <Checkbox
                              checked={isCompleted}
                              onChange={() => handleItemToggle(item.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span
                                  className={cn(
                                    "text-sm font-medium",
                                    isCompleted &&
                                      "line-through text-muted-foreground"
                                  )}
                                >
                                  {item.label}
                                </span>
                                {item.required && (
                                  <Badge
                                    variant="destructive"
                                    className="text-xs"
                                  >
                                    Required
                                  </Badge>
                                )}
                                {item.priority === "high" && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    High Priority
                                  </Badge>
                                )}
                              </div>
                              {item.tip && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {item.tip}
                                </p>
                              )}
                            </div>

                            {!dataCompletedItems.has(item.id) &&
                              item.action && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleActionClick(
                                      item.action?.type || "",
                                      item.id,
                                      item.action?.categoryMetadata
                                    );
                                  }}
                                  className="h-8 w-8 p-0"
                                >
                                  {item.action.icon}
                                </Button>
                              )}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
